/**
 * Date/Time Formatting Utilities
 * 
 * IMPORTANT RULES:
 * - Database stores dates/times in GMT (UTC)
 * - We are in GMT+2 timezone
 * - All dates must be displayed in GMT+2
 * - Date format: YYYY-MM-DD
 * - Timestamp format: YYYY-MM-DD HH:MM:SS (24-hour format)
 * 
 * All functions in this file automatically convert from GMT to GMT+2
 */

/**
 * Convert GMT date to GMT+2 and format as YYYY-MM-DD
 */
export const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    // Add 2 hours to convert from GMT to GMT+2
    const gmtPlus2 = new Date(date.getTime() + 2 * 60 * 60 * 1000);
    const year = gmtPlus2.getUTCFullYear();
    const month = String(gmtPlus2.getUTCMonth() + 1).padStart(2, '0');
    const day = String(gmtPlus2.getUTCDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    return dateString;
  }
};

/**
 * Convert GMT date to GMT+2 and format as YYYY-MM-DD HH:MM:SS (24-hour format)
 */
export const formatDateTime = (dateString?: string | null): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    // Add 2 hours to convert from GMT to GMT+2
    const gmtPlus2 = new Date(date.getTime() + 2 * 60 * 60 * 1000);
    const year = gmtPlus2.getUTCFullYear();
    const month = String(gmtPlus2.getUTCMonth() + 1).padStart(2, '0');
    const day = String(gmtPlus2.getUTCDate()).padStart(2, '0');
    const hours = String(gmtPlus2.getUTCHours()).padStart(2, '0');
    const minutes = String(gmtPlus2.getUTCMinutes()).padStart(2, '0');
    const seconds = String(gmtPlus2.getUTCSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return dateString;
  }
};

/**
 * Convert GMT date to GMT+2 and format as HH:MM:SS (24-hour format, time only)
 */
export const formatTime = (dateString?: string | null): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    // Add 2 hours to convert from GMT to GMT+2
    const gmtPlus2 = new Date(date.getTime() + 2 * 60 * 60 * 1000);
    const hours = String(gmtPlus2.getUTCHours()).padStart(2, '0');
    const minutes = String(gmtPlus2.getUTCMinutes()).padStart(2, '0');
    const seconds = String(gmtPlus2.getUTCSeconds()).padStart(2, '0');
    
    return `${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return dateString;
  }
};

/**
 * Convert GMT date to GMT+2 and format for charts with grouping support
 * Used for chart axis labels and date displays in survey statistics
 */
export const formatChartDate = (date: Date | string, grouping: 'day' | 'week' | 'month' | 'year'): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '-';
    
    // Add 2 hours to convert from GMT to GMT+2
    const gmtPlus2 = new Date(dateObj.getTime() + 2 * 60 * 60 * 1000);
    
    if (grouping === 'day') {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[gmtPlus2.getUTCMonth()];
      const day = gmtPlus2.getUTCDate();
      const year = gmtPlus2.getUTCFullYear();
      return `${month} ${day}, ${year}`;
    } else if (grouping === 'week') {
      const year = gmtPlus2.getUTCFullYear();
      const startOfYear = new Date(Date.UTC(year, 0, 1));
      const days = Math.floor((gmtPlus2.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
      const weekNumber = Math.ceil((days + startOfYear.getUTCDay() + 1) / 7);
      return `W${String(weekNumber).padStart(2, '0')} ${year}`;
    } else if (grouping === 'month') {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[gmtPlus2.getUTCMonth()];
      const year = gmtPlus2.getUTCFullYear();
      return `${month} ${year}`;
    } else { // year
      return gmtPlus2.getUTCFullYear().toString();
    }
  } catch (error) {
    return '-';
  }
};

/**
 * Calculate time difference between two dates and return formatted string
 * Both dates are assumed to be in GMT and will be converted to GMT+2 for display
 */
export const calculateTimeDifference = (startDate?: string | null, endDate?: string | null): string | null => {
  if (!startDate || !endDate) return null;
  
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    
    const diffMs = end.getTime() - start.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMs < 0) return 'Invalid (end before start)';
    
    const parts: string[] = [];
    if (diffDays > 0) parts.push(`${diffDays} day${diffDays !== 1 ? 's' : ''}`);
    if (diffHours % 24 > 0) parts.push(`${diffHours % 24} hour${(diffHours % 24) !== 1 ? 's' : ''}`);
    if (diffMinutes % 60 > 0 && diffDays === 0) parts.push(`${diffMinutes % 60} minute${(diffMinutes % 60) !== 1 ? 's' : ''}`);
    
    return parts.length > 0 ? parts.join(', ') : 'Less than a minute';
  } catch (error) {
    return null;
  }
};

